import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import TransactionModal from '../transactions/TransactionModal';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../../context/AuthContext';
import withPermission from '../../../utils/withPermission';
import Swal from 'sweetalert2';

const TransactionList = ({ accountId, fetchBankDetails, setBankDetails }) => {
    const [transactions, setTransaction] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedTransactionId, setSelectedTransactionId] = useState(null);
    const [selectedTransactionData, setSelectedTransactionData] = useState(null);
    const [errors, setErrors] = useState({});
    const didFetchRef = useRef(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const { id } = useParams();
    const { roles, permissions } = useAuth();
    const WithDeletePermission = withPermission(({ children }) => children, ['transaction-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['transaction-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['transaction-create']);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [banks, setBanks] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [users, setUsers] = useState([]);


    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchTransaction(1, id, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    const fetchTransaction = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/transactions`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                params: {
                    page,
                    account_id: accountId,
                    search: searchQuery,
                },
            });

            const { data, current_page, last_page } = response.data.data;

            setTransaction(data);

            setCurrentPage(current_page - 1);
            setPageCount(last_page);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };
    const fetchCategories = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');

            const response = await axios.get(`${backendUrl}/api/categories?type=expense`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                skipLoader: true,

            });
            setCategories(response.data.category);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchBanks = async (accountType = '') => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // ✅ Remove check for accountType, allowing fetching all banks
            let url = `${backendUrl}/api/bank-accounts`;
            if (accountType) {
                url += `?type=${encodeURIComponent(accountType)}`; // Fetch specific type if provided
            }

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });

            setBanks(response.data.account); // ✅ Update state with all banks

        } catch (error) {
            console.error('Error fetching banks:', error);
        }
    };
    const fetchClients = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts?type=client`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });
            setClients(response.data.Data || []); // Ensure safe access
        } catch (error) {
            console.error('Error fetching clients:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Failed to fetch clients. Please try again.',
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
        }
    };
    const fetchVendors = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/contacts?type=vendor`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });
            setVendors(response.data.Data || []); // Ensure safe access
        } catch (error) {
            console.error('Error fetching vendors:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Failed to fetch vendors. Please try again.',
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
        }
    };
    
    const fetchUsers = async (page = 1) => {
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
    
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
    
                const token = userInfo?.token;
                const response = await axios.get(`${backendUrl}/api/users`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true,
    
                });
                // const { data, current_page, last_page } = response.data.users;
    
                setUsers(response.data.users);
    
    
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
    const fetchProjects = async (clientId) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (!clientId) {
                setProjects([]); // ✅ Clear projects if no client selected
                return;
            }

            const response = await axios.get(`${backendUrl}/api/projects?client_id=${clientId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                skipLoader: true,

            });

            setProjects(response.data.Data || []); // ✅ Store only the selected client's projects
        } catch (error) {
            console.error("Error fetching projects:", error);
            setProjects([]); // ✅ Ensure dropdown is cleared on error
        }
    };

    const addTransaction = async (newTransaction) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/transactions?account_id=${accountId}`, newTransaction, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const createdTransaction = response.data?.data;

            if (createdTransaction) {
                setTransaction((prevTransaction) => [...prevTransaction, createdTransaction]);
                setBankDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails; // Handle case when details are not yet loaded

                    let updatedBalance = parseFloat(prevDetails.opening_balance) || 0;
                    const transactionAmount = parseFloat(createdTransaction.amount) || 0;

                    if (createdTransaction.type === 'income') {
                        updatedBalance += transactionAmount;
                    } else if (createdTransaction.type === 'expense') {
                        updatedBalance -= transactionAmount;
                    }

                    return {
                        ...prevDetails,
                        opening_balance: updatedBalance.toFixed(2),
                    };
                });
            } else {
                console.error('Creation failed, no data returned.');
            }


            handleCloseModal();
            // fetchBankDetails();
        } catch (error) {
            if (error.response?.data?.message) {
                return error.response.data.message; // Return validation errors
            } else {
                console.error('Error adding transaction:', error);
            }
        }
    };

    const editTransaction = async (transactionId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const existingTransaction = transactions.find((t) => t.id === transactionId);
            if (!existingTransaction) {
                console.error("Transaction not found locally.");
                return;
            }
            const response = await axios.put(`${backendUrl}/api/transactions/${transactionId}`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const updatedTransaction = response.data?.data;

            if (updatedTransaction) {
                setTransaction((prevTransaction) =>
                    prevTransaction.map((transaction) => (transaction.id === updatedTransaction.id ? updatedTransaction : transaction))
                );
                setBankDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails;

                    let updatedBalance = parseFloat(prevDetails.opening_balance) || 0;
                    const oldAmount = parseFloat(existingTransaction.amount) || 0;
                    const newAmount = parseFloat(updatedTransaction.amount) || 0;

                    // **Reverse Old Transaction Effect**
                    if (existingTransaction.type === 'income') {
                        updatedBalance -= oldAmount;
                    } else if (existingTransaction.type === 'expense') {
                        updatedBalance += oldAmount;
                    }

                    // **Apply New Transaction Effect**
                    if (updatedTransaction.type === 'income') {
                        updatedBalance += newAmount;
                    } else if (updatedTransaction.type === 'expense') {
                        updatedBalance -= newAmount;
                    }

                    return {
                        ...prevDetails,
                        opening_balance: updatedBalance.toFixed(2),
                    };
                });
            } else {
                console.error('Update failed, no data returned.');
            }

            handleCloseModal();
        } catch (error) {
            if (error.response && error.response.data.message) {
                setErrors(error.response.data.message);
            } else {
                console.error('Error updating transaction:', error);
            }
        }
    };

    const deleteTransaction = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;

                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;

                    await axios.delete(` ${backendUrl}/api/transactions/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setTransaction(transactions.filter(transaction => transaction.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The transaction has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting transaction:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete transaction. Please try again.",
                        "error"
                    );
                }
            }
        });
    };

    // const handleShowModal = (transactionId = null) => {
    //     if (transactionId) {
    //         const transactionToEdit = transactions.find(transaction => transaction.id === transactionId);
    //         setSelectedTransactionData(transactionToEdit || null); // Set data for editing
    //     } else {
    //         setSelectedTransactionData(null); // Clear form data for creating new transaction
    //         setErrors({}); // Clear any errors
    //     }
    //     setSelectedTransactionId(transactionId);
    //     setShowModal(true);
    // };

    const handleShowModal = (transactionId = null, mode = "view") => {
        console.log("handleShowModal called with transactionId:", transactionId, "mode:", mode);

        if (transactionId !== null) {
            const transactionToEdit = transactions.find(transaction => transaction.id === transactionId);
            setSelectedTransactionData(transactionToEdit || null);
            setSelectedTransactionId(transactionId);
        } else {
            setSelectedTransactionData(null);
            setSelectedTransactionId(null);
            setErrors({});
        }

        // 👇 set mode here
        setIsEditMode(mode === "edit");

        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedTransactionId(null);
        setSelectedTransactionData(null);
        setErrors({});
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchTransaction(newPage);
    };

    return (
        <React.Fragment>
            {/* Conditionally wrap content in card and card-body only if type is 'deal' */}
            <div>
                <div>
                    <h4 className="mb-sm-0 font-size-18"></h4>
                    <div className="d-flex justify-content-end mb-2">
                        {/* Search Box */}
                        <div className="search-box me-2">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="form-control font-size-13"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                                <i className="bx bx-search-alt search-icon" />
                            </div>
                        </div>

                        {/* Add Transaction Button */}
                        <WithCreatePermission>
                            <button
                                type="button"
                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-plus"

                                onClick={() => handleShowModal(null, "edit")}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                id="tool"
                                title="Add Transaction"
                            >
                                <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                            </button>
                        </WithCreatePermission>
                    </div><br />

                    <table className="table table-hover datatable dt-responsive nowrap view-table font-size-13" >
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Date</th>
                                <th scope="col">Type</th>
                                <th scope="col">Transaction</th>
                                <th scope="col">Amount</th>
                                {/* <th scope="col">Category</th> */}
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length > 0 ? (
                                transactions.map((transaction, index) => (
                                    <tr key={transaction.id}>
                                        <td>{index + 1}</td>
                                        <td onClick={() => handleShowModal(transaction.id, "view")}>
                                            {transaction.date}</td>
                                        <td onClick={() => handleShowModal(transaction.id, "view")}>

                                            {transaction.type}
                                        </td>
                                        <td>{transaction.transaction}</td>
                                        <td>{transaction.amount}</td>
                                        {/* <td>{transaction.category?.name|| transaction.project?.name}</td> */}
                                        <td>
                                            <div className="dropdown">
                                                <i
                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                    style={{ cursor: 'pointer' }}
                                                ></i>
                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">

                                                    <li>
                                                        <WithEditPermission>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => handleShowModal(transaction.id, "edit")}
                                                            >
                                                                Edit
                                                            </button>
                                                        </WithEditPermission>
                                                    </li>
                                                    <li>
                                                        <WithDeletePermission>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => deleteTransaction(transaction.id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </WithDeletePermission>
                                                    </li>
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No transactions available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="d-flex justify-content-end pagination-sm">
                        <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />

                    </div>

                </div>
            </div>


            {/* Render the TransactionModal */}
            <TransactionModal
                showModal={showModal}
                handleClose={handleCloseModal}
                transactionId={selectedTransactionId}
                transactionData={selectedTransactionData}
                addTransaction={addTransaction}
                editTransaction={editTransaction}
                errors={errors}
                setErrors={setErrors}
                clients={clients}
                fetchClients={fetchClients}
                projects={projects}
                fetchProjects={fetchProjects}
                categories={categories}
                fetchCategories={fetchCategories}
                banks={banks}
                fetchBanks={fetchBanks}
                vendors={vendors}
                fetchVendors={fetchVendors}
                setBanks={setBanks}
                accountId={accountId}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                users={users}
                fetchUsers={fetchUsers}
            />
        </React.Fragment>
    );


};
export default TransactionList;








